import {
  Box,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  styled,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import BankIcon from "../../../../assets/icons/BankIcon";
import CardIcon from "../../../../assets/icons/CardIcon";
import { TOKENIZED_BANK } from "../../../../Constants/constants";
import { setIframeURL } from "../../../../Redux/Slices/PaymentSlice";
import { formatExpirationDate, getBannerMessageByKey, getFlags } from "../../../../Services/helper";
import { AutoPayDivider, OutlinedButton } from "../../../../Styles/GlobalStyles";
import { Theme } from "../../../../Styles/GlobalTheme";
import Surcharge from "../Surcharge/Surcharge";

const PaymentType = ({
  paymentMethods,
  paymentMethodsLoading,
  buttonId,
  setButtonId,
  paymentMethodsError,
  isAutopayScreen = false,
}: any) => {
  const location = useLocation();
  const preselectedMethodId = location?.state?.preselectedMethodId;

  const { ndsObj, activeAccount } = useSelector((state: any) => state.customerInfoDetails);
  const { paymentsMigrationToNDS, autopaySurchargeDetails } = useSelector(
    (state: any) => state.customerPlanDetails
  );
  const { autoPayData } = useSelector((state: any) => state?.autopayPaperlessDetails);
  const { envFlags } = useSelector((state: any) => state?.userInfo);
  const sideCoreBannerMessages = useSelector(
    (state: any) => state?.sideCoreContent?.bannerMessages
  );
  const isAutopayEnrolled = !!(autoPayData && Object.keys(autoPayData)?.length > 0);
  const navigate = useNavigate();
  const handleEnrollAutopay = () => {
    navigate("/billing/signupautopay", {
      replace: false,
      state: {
        fromPaymentSuccess: false,
        preselectedMethodId: buttonId,
      },
    });
  };
  const SURCHARGE_BANNER_TEXT = getBannerMessageByKey(
    "surcharge-banner-make-payment",
    sideCoreBannerMessages
  );
  const ENABLE_SURCHARGE = getFlags("enable_surcharge", envFlags);
  const dispatch = useDispatch();

  const isNDS = ndsObj?.isNDS || paymentsMigrationToNDS;
  const _buttonId = isNDS ? "new card" : "new bank";

  // Sort payment methods: Active first, Expired last
  const sortedPaymentMethods = useMemo(() => {
    return [...(paymentMethods || [])].sort((first: any, second: any) => {
      return (first?.isPaymentExpired ? 1 : 0) - (second?.isPaymentExpired ? 1 : 0);
    });
  }, [paymentMethods]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setButtonId((event.target as HTMLInputElement).value);
    dispatch(setIframeURL(""));
  };

  const filterId = () => {
    // Filter out expired payment methods
    const nonExpiredMethods = paymentMethods?.filter((method: any) => !method?.isPaymentExpired);

    // If no valid methods exist, default to new payment option
    if (!nonExpiredMethods || nonExpiredMethods.length === 0) {
      setButtonId(_buttonId);
      return;
    }

    if (autoPayData && Object.keys(autoPayData)?.length > 0) {
      if (!isNDS) {
        let filterNonNdsId = nonExpiredMethods?.filter(
          (method: any) => method?.id === autoPayData?.paymentMethodId
        );
        setButtonId(filterNonNdsId?.[0]?.id || nonExpiredMethods[0]?.id || _buttonId);
      } else {
        let filterNdsId = nonExpiredMethods?.filter(
          (method: any) => method?.id === autoPayData?.id
        );
        setButtonId(filterNdsId?.[0]?.id || nonExpiredMethods[0]?.id || _buttonId);
      }
    } else {
      const preSelectedPaymentMethod = nonExpiredMethods?.find(
        (method: any) => method?.id === preselectedMethodId
      );
      setButtonId(preSelectedPaymentMethod?.id || nonExpiredMethods[0]?.id || _buttonId);
    }
  };

  const handleSetDefaultValue = () => {
    switch (true) {
      case paymentMethods?.length > 0:
        filterId();
        break;
      case isNDS:
        setButtonId("new card");
        break;
      case !isNDS:
        setButtonId("new bank");
        break;
      default:
        setButtonId("");
        break;
    }
  };

  const handleSetIcon = (method: any, isExpired: boolean) => {
    switch (true) {
      case method?.paymentMethodType === TOKENIZED_BANK:
        return (
          <BankIcon
            sx={{
              color: isExpired
                ? Theme.palette.customcolor.disabledIcons
                : Theme.palette.primary.main,
              width: 20,
              height: 20,
            }}
          />
        );
      default:
        return (
          <CardIcon
            sx={{
              color: isExpired
                ? Theme.palette.customcolor.disabledIcons
                : Theme.palette.primary.main,
              width: 20,
              height: 20,
            }}
          />
        );
    }
  };
  const bannerText =
    isAutopayScreen && autopaySurchargeDetails.surchargeEligible
      ? autopaySurchargeDetails?.paymentScreenMessage
      : SURCHARGE_BANNER_TEXT?.content?.subheading?.[0]?.text || "";

  const displaySurchargeBanner = isAutopayScreen
    ? autopaySurchargeDetails?.surchargeEligible
    : true;

  useEffect(() => {
    !paymentMethodsLoading && handleSetDefaultValue();
  }, [paymentMethodsLoading]);

  return (
    <>
      <Stack>
        <FormControl>
          <RadioGroup
            aria-labelledby="demo-controlled-radio-buttons-group"
            name="controlled-radio-buttons-group"
            value={buttonId}
            onChange={handleChange}>
            {!paymentMethodsError &&
              sortedPaymentMethods?.map((method: any) => {
                const isExpired = method?.isPaymentExpired;
                const isSelected = buttonId === method?.id;
                return (
                  <div key={method?.id} data-testid="cards">
                    <PaymentMethodRow>
                      <PaymentMethodInfo>
                        <Radio
                          checked={isSelected}
                          onChange={handleChange}
                          value={method?.id}
                          disabled={isExpired}
                          sx={{ p: 0, mr: 1.5 }}
                        />
                        <Box display="flex" alignItems="center" mr={0.5}>
                          {handleSetIcon(method, isExpired)}
                        </Box>
                        <Box display="flex" flexDirection="column" justifyContent="center">
                          <Typography
                            px={method?.cardType?.toLowerCase() === "visa" ? 0 : 1}
                            fontSize={{ xs: "13px", md: "15px" }}
                            lineHeight={1.2}>
                            {method?.nameOnTheCard}
                          </Typography>
                          {method?.expirationDate && (
                            <Typography
                              px={method?.cardType?.toLowerCase() === "visa" ? 0 : 1}
                              fontSize={{ xs: "10px", md: "12px" }}
                              color={
                                isExpired
                                  ? Theme.palette.warning.main
                                  : Theme.palette.customcolor.profileTypoGraphyColor
                              }
                              lineHeight={1.1}>
                              {isExpired ? "Expired on: " : "Expires on: "}
                              {formatExpirationDate(method?.expirationDate)}
                            </Typography>
                          )}
                        </Box>
                      </PaymentMethodInfo>
                      {!isAutopayScreen && !isAutopayEnrolled && isSelected && !isExpired && activeAccount && (
                        <OutlinedButton onClick={handleEnrollAutopay}>
                          {" "}
                          ENROLL IN AUTOPAYMENT
                        </OutlinedButton>
                      )}
                    </PaymentMethodRow>
                    {ENABLE_SURCHARGE &&
                      isSelected &&
                      method?.type.toLowerCase() === "credit card" &&
                      !isExpired &&
                      displaySurchargeBanner && <Surcharge bannerText={bannerText} />}
                    <AutoPayDivider />
                  </div>
                );
              })}

            {!isNDS ? (
              <>
                <FormControlLabel value="new bank" control={<Radio />} label="New Payment method" />
                {ENABLE_SURCHARGE && buttonId === "new bank" && displaySurchargeBanner && (
                  <Surcharge bannerText={bannerText} />
                )}
              </>
            ) : (
              <>
                <FormControlLabel
                  value="new card"
                  control={<Radio />}
                  label="New Credit/Debit card"
                />
                {ENABLE_SURCHARGE && buttonId === "new card" && displaySurchargeBanner && (
                  <Surcharge bannerText={bannerText} />
                )}
                <AutoPayDivider />
                <FormControlLabel value="new bank" control={<Radio />} label="New bank account" />
              </>
            )}
          </RadioGroup>
        </FormControl>
      </Stack>
    </>
  );
};
const PaymentMethodRow = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  width: "100%",
  minHeight: 56,
  gap: 0,
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
    gap: theme.spacing(1),
  },
}));

const PaymentMethodInfo = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  flex: 1,
  minHeight: 40,
  width: "auto",
  justifyContent: "flex-start",
  [theme.breakpoints.down("sm")]: {
    width: "100%",
  },
}));
export default PaymentType;
